import React, { useState } from 'react';
import { UploadIcon, FileTextIcon, SearchIcon, FilterIcon, EyeIcon, DownloadIcon, LinkIcon, XIcon, PlusIcon, CalendarIcon, TagIcon } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Document } from '../data/types';
export function Documents() {
  const {
    state,
    dispatch,
    navigate,
    canCreate
  } = useApp();
  const {
    documents,
    landRecords,
    language
  } = state;
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [newDoc, setNewDoc] = useState({
    title: '',
    type: 'Deed' as Document['type'],
    date: '',
    tags: '',
    linkedRecordId: ''
  });
  const filteredDocuments = documents.filter(doc => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      if (!doc.title.toLowerCase().includes(search) && !doc.type.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (typeFilter && doc.type !== typeFilter) return false;
    return true;
  });
  const handleUpload = () => {
    const newDocument: Document = {
      id: `doc-${Date.now()}`,
      title: newDoc.title,
      type: newDoc.type,
      date: newDoc.date || new Date().toISOString().split('T')[0],
      tags: newDoc.tags.split(',').map(t => t.trim()).filter(Boolean),
      linkedRecordId: newDoc.linkedRecordId || undefined
    };
    dispatch({
      type: 'ADD_DOCUMENT',
      payload: newDocument
    });
    setShowUploadModal(false);
    setNewDoc({
      title: '',
      type: 'Deed',
      date: '',
      tags: '',
      linkedRecordId: ''
    });
  };
  const getLinkedRecordName = (recordId?: string) => {
    if (!recordId) return null;
    const record = landRecords.find(r => r.id === recordId);
    return record ? `${record.landId} - ${record.landName}` : null;
  };
  const getTypeColor = (type: Document['type']) => {
    switch (type) {
      case 'Deed':
        return 'bg-blue-100 text-blue-700';
      case 'Mutation':
        return 'bg-purple-100 text-purple-700';
      case 'Khajna':
        return 'bg-green-100 text-green-700';
      case 'Litigation':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  return <div className="p-4 lg:p-6 space-y-4" data-id="element-158">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-159">
        <div data-id="element-160">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-161">
            {language === 'en' ? 'Documents' : 'দলিলপত্র'}
          </h1>
          <p className="text-gray-600 mt-1" data-id="element-162">
            {filteredDocuments.length}{' '}
            {language === 'en' ? 'documents' : 'দলিল'}
          </p>
        </div>
        {canCreate() && <button onClick={() => setShowUploadModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-163">
            <UploadIcon className="w-5 h-5" data-id="element-164" />
            <span data-id="element-165">{language === 'en' ? 'Upload Document' : 'দলিল আপলোড'}</span>
          </button>}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4" data-id="element-166">
        <div className="flex flex-col sm:flex-row gap-4" data-id="element-167">
          <div className="flex-1 relative" data-id="element-168">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-169" />
            <input type="text" placeholder={language === 'en' ? 'Search documents...' : 'দলিল খুঁজুন...'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-170" />
          </div>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-171">
            <option value="" data-id="element-172">All Types</option>
            <option value="Deed" data-id="element-173">Deed</option>
            <option value="Mutation" data-id="element-174">Mutation</option>
            <option value="Khajna" data-id="element-175">Khajna</option>
            <option value="Litigation" data-id="element-176">Litigation</option>
            <option value="Other" data-id="element-177">Other</option>
          </select>
        </div>
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-178">
        <div className="overflow-x-auto" data-id="element-179">
          <table className="w-full" data-id="element-180">
            <thead data-id="element-181">
              <tr className="bg-gray-50 border-b border-gray-200" data-id="element-182">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-183">
                  Document
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-184">
                  Type
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell" data-id="element-185">
                  Date
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell" data-id="element-186">
                  Linked Record
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell" data-id="element-187">
                  Tags
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-188">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-189">
              {filteredDocuments.map(doc => <tr key={doc.id} className="hover:bg-gray-50 transition-colors" data-id="element-190">
                  <td className="px-4 py-3" data-id="element-191">
                    <div className="flex items-center gap-3" data-id="element-192">
                      <div className="p-2 bg-gray-100 rounded-lg" data-id="element-193">
                        <FileTextIcon className="w-5 h-5 text-gray-600" data-id="element-194" />
                      </div>
                      <span className="font-medium text-gray-900" data-id="element-195">
                        {doc.title}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-196">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getTypeColor(doc.type)}`} data-id="element-197">
                      {doc.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell" data-id="element-198">
                    <span className="text-gray-600 text-sm" data-id="element-199">{doc.date}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell" data-id="element-200">
                    {doc.linkedRecordId ? <button onClick={() => {
                  dispatch({
                    type: 'SET_SELECTED_RECORD',
                    payload: doc.linkedRecordId!
                  });
                  navigate('land-record-details', doc.linkedRecordId);
                }} className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1" data-id="element-201">
                        <LinkIcon className="w-3 h-3" data-id="element-202" />
                        {getLinkedRecordName(doc.linkedRecordId)?.split(' - ')[0]}
                      </button> : <span className="text-gray-400 text-sm" data-id="element-203">—</span>}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell" data-id="element-204">
                    <div className="flex flex-wrap gap-1" data-id="element-205">
                      {doc.tags.slice(0, 2).map(tag => <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs" data-id="element-206">
                          {tag}
                        </span>)}
                      {doc.tags.length > 2 && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs" data-id="element-207">
                          +{doc.tags.length - 2}
                        </span>}
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-208">
                    <div className="flex items-center justify-end gap-1" data-id="element-209">
                      <button className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="View" data-id="element-210">
                        <EyeIcon className="w-4 h-4" data-id="element-211" />
                      </button>
                      <button className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Download" data-id="element-212">
                        <DownloadIcon className="w-4 h-4" data-id="element-213" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {filteredDocuments.length === 0 && <div className="px-4 py-12 text-center" data-id="element-214">
            <FileTextIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" data-id="element-215" />
            <p className="text-gray-500" data-id="element-216">
              {language === 'en' ? 'No documents found' : 'কোনো দলিল পাওয়া যায়নি'}
            </p>
          </div>}
      </div>

      {/* Upload Modal */}
      {showUploadModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-217">
          <div className="bg-white rounded-xl w-full max-w-lg" data-id="element-218">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-219">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-220">
                Upload Document
              </h2>
              <button onClick={() => setShowUploadModal(false)} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg" data-id="element-221">
                <XIcon className="w-5 h-5" data-id="element-222" />
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-223">
              <div data-id="element-224">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-225">
                  Document Title *
                </label>
                <input type="text" value={newDoc.title} onChange={e => setNewDoc({
              ...newDoc,
              title: e.target.value
            })} placeholder="e.g., Sale Deed – DR-2026-01457" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-226" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-227">
                <div data-id="element-228">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-229">
                    Type *
                  </label>
                  <select value={newDoc.type} onChange={e => setNewDoc({
                ...newDoc,
                type: e.target.value as Document['type']
              })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-230">
                    <option value="Deed" data-id="element-231">Deed</option>
                    <option value="Mutation" data-id="element-232">Mutation</option>
                    <option value="Khajna" data-id="element-233">Khajna</option>
                    <option value="Litigation" data-id="element-234">Litigation</option>
                    <option value="Other" data-id="element-235">Other</option>
                  </select>
                </div>
                <div data-id="element-236">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-237">
                    Date
                  </label>
                  <input type="date" value={newDoc.date} onChange={e => setNewDoc({
                ...newDoc,
                date: e.target.value
              })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-238" />
                </div>
              </div>
              <div data-id="element-239">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-240">
                  Tags (comma-separated)
                </label>
                <input type="text" value={newDoc.tags} onChange={e => setNewDoc({
              ...newDoc,
              tags: e.target.value
            })} placeholder="e.g., original, registered, pending" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-241" />
              </div>
              <div data-id="element-242">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-243">
                  Link to Land Record
                </label>
                <select value={newDoc.linkedRecordId} onChange={e => setNewDoc({
              ...newDoc,
              linkedRecordId: e.target.value
            })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-244">
                  <option value="" data-id="element-245">No linked record</option>
                  {landRecords.map(r => <option key={r.id} value={r.id} data-id="element-246">
                      {r.landId} - {r.landName}
                    </option>)}
                </select>
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-247">
                <UploadIcon className="w-10 h-10 mx-auto text-gray-400 mb-2" data-id="element-248" />
                <p className="text-gray-600" data-id="element-249">
                  Drag & drop file here or click to browse
                </p>
                <p className="text-sm text-gray-400 mt-1" data-id="element-250">
                  PDF, JPG, PNG up to 10MB
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200" data-id="element-251">
              <button onClick={() => setShowUploadModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" data-id="element-252">
                Cancel
              </button>
              <button onClick={handleUpload} disabled={!newDoc.title} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-253">
                Upload
              </button>
            </div>
          </div>
        </div>}
    </div>;
}